import { request, RequestDocument } from 'graphql-request';
import { DocumentNode, parse } from 'graphql';
import GraphqlResponseError from '@/errors/GraphqlResponseError';
import { AuthService } from '@/services/auth.service';
import { Dict } from '@/types/utility';
import JwtExpiredError from '@/errors/JwtExpiredError';
import LocationHelper from '@/utils/LocationHelper';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const parseJsonModule = require('parse-json');
const parseJson = typeof parseJsonModule === 'function' ? parseJsonModule : parseJsonModule.default;

// Constant timeout of 2 minutes (120000 milliseconds)
const GRAPHQL_REQUEST_TIMEOUT = 120000;

const ensureDocumentNode = (document: RequestDocument): DocumentNode => {
  return typeof document === 'string' ? parse(document) : (document as DocumentNode);
};

const makeGraphqlRequestBase = (url: string, requestHeaders: HeadersInit) => {
  return async (query: RequestDocument, variables: Dict<unknown>): Promise<unknown> => {
    // Create AbortController for timeout
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, GRAPHQL_REQUEST_TIMEOUT);

    try {
      const sanitizedQuery = ensureDocumentNode(query);

      const result = await request({
        url,
        document: sanitizedQuery,
        variables,
        requestHeaders,
        signal: abortController.signal as any,
      });
      return result;
    } catch (e: any) {
      // Handle timeout/abort errors
      if (e.name === 'AbortError') {
        throw new Error(
          `GraphQL request timed out after ${GRAPHQL_REQUEST_TIMEOUT / 1000} seconds`,
        );
      }

      if (e.response?.errors?.length) {
        if (e.response.errors[0].extensions?.code === 'invalid-jwt') {
          throw new JwtExpiredError();
        }

        try {
          const payload: any = parseJson(e.response.errors[0].message);

          throw new GraphqlResponseError(
            payload.message instanceof Array ? payload.message[0] : payload.message,
            payload,
          );
        } catch (parseError) {
          // Check if parseJson threw a JSONError (by name since types aren't available)
          if (parseError instanceof Error && parseError.name === 'JSONError') {
            throw new GraphqlResponseError(e.response.errors[0].message, {
              message: e.response.errors[0].message,
              fields: null,
            });
          }
          throw parseError;
        }
      }

      throw e;
    } finally {
      // Clear the timeout to prevent memory leaks
      clearTimeout(timeoutId);
    }
  };
};

export const makeGraphqlRequestCloud = async (
  query: RequestDocument,
  variables: Dict<unknown>,
  attempt = 1,
  shareToken?: string,
  sharedObjectId?: number,
): Promise<unknown> => {
  const token = shareToken
    ? await AuthService.getHasuraAnonymousToken(shareToken, String(sharedObjectId))
    : await AuthService.tokenRefreshedIfOld();

  try {
    const graphqlUrl = `${LocationHelper.getBaseUrl() ?? ''}/hasura/v1/graphql`;
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const result = await makeGraphqlRequestBase(graphqlUrl, headers)(query, variables);
    return result;
  } catch (e: any) {
    if (e instanceof JwtExpiredError && attempt < 2) {
      return makeGraphqlRequestCloud(query, variables, attempt + 1, shareToken, sharedObjectId);
    }
    throw e;
  }
};

export const makeGraphqlRequestApp = async (
  query: RequestDocument,
  variables: Dict<unknown>,
): Promise<unknown> => {
  const token = await AuthService.tokenRefreshedIfOld();
  const graphqlUrl = `${LocationHelper.getBaseUrl() ?? ''}/gql/v1/query`;
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  return makeGraphqlRequestBase(graphqlUrl, headers)(query, variables);
};
